import React from 'react';
import { isPdfPreviewable } from '../pdf/PdfPreviewProvider';
import DsButton from '../DsButton';
import DsModal from '../DsModal';
import { SkeletonLine } from '../Skeleton';
import { formatDateTimeJST } from '../../../utils/datetime';
import {
  materialDownloadActorIdentity,
  materialDownloadActorName,
  materialDownloadActorRoleLabel,
} from './eventMaterialExplorerUtils';

export function MaterialHistoryDialog({
  historyModal,
  downloadLogModal,
  modalLoading,
  modalRows,
  loadingFallback,
  onClose,
}) {
  if (!historyModal && !downloadLogModal) return null;
  return (
    <DsModal
      open
      onClose={onClose}
      title={historyModal ? '資料履歴' : 'ダウンロード履歴'}
      size="md"
      className="eme-modal"
    >
      {modalLoading ? (
        loadingFallback || <div className="eme-empty"><SkeletonLine width="60%" height={14} /></div>
      ) : modalRows.length === 0 ? (
        <div className="eme-empty">履歴はありません</div>
      ) : (
        <div className="eme-modal-list">
          {modalRows.map((row) => (
            <div key={row.id} className="eme-modal-row">
              <strong>
                {historyModal
                  ? (row.version_label || `v${row.version_number}`)
                  : `${materialDownloadActorRoleLabel(row)}: ${materialDownloadActorName(row)}`}
              </strong>
              <span>{historyModal ? row.file_name : materialDownloadActorIdentity(row) || '-'}</span>
              <small>{formatDateTimeJST(row.created_at)}</small>
            </div>
          ))}
        </div>
      )}
    </DsModal>
  );
}

export function ConfirmMaterialDialog({
  confirmDialog,
  busy,
  onCancel,
  onConfirm,
}) {
  return (
    <DsModal
      open={!!confirmDialog}
      onClose={onCancel}
      title={confirmDialog?.title}
      size="sm"
      dismissible={!busy}
      footer={(
        <DsButton block variant="danger" onClick={onConfirm} disabled={busy}>
          {confirmDialog?.confirmLabel || '実行'}
        </DsButton>
      )}
    >
      <p>{confirmDialog?.message}</p>
    </DsModal>
  );
}

export function MaterialActionMenu({
  actionMenu,
  isAdmin,
  canManage,
  canDownload,
  canViewAllLogs,
  replaceInputRef,
  runMenuAction,
  toggleFolderShare,
  renameFolder,
  deleteFolder,
  toggleMaterialShare,
  downloadMaterial,
  setReplaceTarget,
  openVersions,
  openDownloadLogs,
  deleteMaterialRow,
}) {
  if (!actionMenu) return null;
  return (
    <div
      className="eme-context-menu"
      role="menu"
      style={{ left: `${actionMenu.x}px`, top: `${actionMenu.y}px` }}
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      {actionMenu.itemType === 'folder' ? (
        <>
          {(!isAdmin || canManage) && actionMenu.item.can_change_share ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => runMenuAction(() => toggleFolderShare(actionMenu.item, !actionMenu.item.share_with_artists))}
            >
              <i className={`fa-solid ${actionMenu.item.share_with_artists ? 'fa-lock' : 'fa-share-nodes'}`} />
              {actionMenu.item.share_with_artists ? '非共有にする' : '共有にする'}
            </button>
          ) : null}
          {(!isAdmin || canManage) && actionMenu.item.can_rename ? (
            <button type="button" role="menuitem" onClick={() => runMenuAction(() => renameFolder(actionMenu.item))}>
              <i className="fa-solid fa-pen" />
              名前変更
            </button>
          ) : null}
          {(!isAdmin || canManage) && actionMenu.item.can_delete ? (
            <button
              type="button"
              role="menuitem"
              className="is-danger"
              onClick={() => runMenuAction(() => deleteFolder(actionMenu.item))}
            >
              <i className="fa-solid fa-trash" />
              削除
            </button>
          ) : null}
        </>
      ) : (
        <>
          {isAdmin && canManage && !actionMenu.item.folder_id ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => runMenuAction(() => toggleMaterialShare(actionMenu.item, !actionMenu.item.share_with_artists))}
            >
              <i className={`fa-solid ${actionMenu.item.share_with_artists ? 'fa-lock' : 'fa-share-nodes'}`} />
              {actionMenu.item.share_with_artists ? '非共有にする' : '共有にする'}
            </button>
          ) : null}
          {canDownload ? (
            <button type="button" role="menuitem" onClick={() => runMenuAction(() => downloadMaterial(actionMenu.item))}>
              <i className={`fa-solid ${isPdfPreviewable({ fileName: actionMenu.item.file_name, mimeType: actionMenu.item.mime_type || actionMenu.item.content_type }) ? 'fa-eye' : 'fa-download'}`} />
              {isPdfPreviewable({ fileName: actionMenu.item.file_name, mimeType: actionMenu.item.mime_type || actionMenu.item.content_type }) ? 'プレビュー' : 'ダウンロード'}
            </button>
          ) : null}
          {(canManage || (!isAdmin && actionMenu.item.can_replace)) ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => runMenuAction(() => {
                setReplaceTarget(actionMenu.item);
                replaceInputRef.current?.click();
              })}
            >
              <i className="fa-solid fa-rotate" />
              差し替え
            </button>
          ) : null}
          {(canManage || (!isAdmin && actionMenu.item.can_replace)) ? (
            <button type="button" role="menuitem" onClick={() => runMenuAction(() => openVersions(actionMenu.item))}>
              <i className="fa-solid fa-clock-rotate-left" />
              履歴
            </button>
          ) : null}
          {(canViewAllLogs || (!isAdmin && actionMenu.item.can_view_download_logs)) ? (
            <button type="button" role="menuitem" onClick={() => runMenuAction(() => openDownloadLogs(actionMenu.item))}>
              <i className="fa-solid fa-chart-simple" />
              ダウンロード履歴
            </button>
          ) : null}
          {(canManage || (!isAdmin && actionMenu.item.can_delete)) ? (
            <button
              type="button"
              role="menuitem"
              className="is-danger"
              onClick={() => runMenuAction(() => deleteMaterialRow(actionMenu.item))}
            >
              <i className="fa-solid fa-trash" />
              削除
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
